import React from 'react'
import PropTypes from 'prop-types'
import {submitFiles} from './SubmitFiles'
import {loggerEffect} from '../effects'
import SystemLogger from '../SystemLogger'

// If you need to bypass all the machinery in this component except the storage
// ref assignment, supply a function to the 'submit' prop -- Chris

/**
 * This component creates a drag and drop box that lets a user upload files.
 * It handles file uploads by using the submitFiles function.
 * Requires the following props to be passed in
 * @param collection The collection of the associated document in firebase.
 * @param id The id of the document this file is expected to be part of. A reference to this file will be written to the corresponding document
 * @param type The type of file this is. Valid values are: "photo", "file", "report", "misc", "thumbnail"
 * The function will append file data to different parts of the document based on this,
 * and it will be stored in a folder with other files of the same type.
 *
 * The uploader handles multiple files by default, but if you'd like to restrict
 * it to only one file, use the `singleUpload` flag.
 */


const allowedMimeTypes = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/tiff',
  'image/bmp',
  'image/heic',
  'application/pdf', // PDF
  'application/msword', // DOC
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.ms-excel', // XLS
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'application/vnd.ms-powerpoint', // PPT
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
  'application/csv',
  'application/x-csv',
  'text/csv',
  'text/comma-separated-values',
  'text/x-comma-separated-values',
  'text/tab-separated-values'
];

export default class FileUploader extends React.Component {

  

  static propTypes = {
    helperMessage: PropTypes.func,
    id: PropTypes.string,
    type: PropTypes.oneOf([`photo`, `file`, `report`, `misc`, `thumbnail`, `avatar`, `logo`]).isRequired,
    collection: PropTypes.string,
    updateStatus: PropTypes.func,
    submit: PropTypes.func,
    sectionId: PropTypes.string,
    fieldIndex: PropTypes.string,
    done: PropTypes.func,
    disabled: PropTypes.bool,
    skipDuplicate: PropTypes.bool,               // ✅ New prop
    existedFiles: PropTypes.arrayOf(PropTypes.string) // ✅ New prop
  }

  static defaultProps = {
    skipDuplicate: false,
    existedFiles: []
  }  

  state = {
    backgroundColor: `#E9ECEF`,
    color: `black`,
    uploaded: [],
    files: [],
    textarrays: [],
    names: [],
  }

  componentDidMount = () => {
    this.fileinput = React.createRef()
    // Without this the drop() function fails because .current is undefined
    if (!this.fileinput.current) this.fileinput.current = {}

    //loggerEffect(setLogger)

  }

  componentDidUpdate() {
    if (this.state.files.length > 0) {
      this.upload()
    }
  }

  // fires when the user submits a list of CSV files
  upload = async () => {
    await submitFiles({files: this.state.files, ...this.props})
    this.setState({files: []})
  }

  // catches files the user drops and uses the most up-to-date file handler the browser suppports
  drop = async (e) => {
    this.setState({ dragOver: false })
    e.preventDefault()
    e.persist()

    let files
    if (e.dataTransfer) {
      files = e.dataTransfer.items
        ? Array.from(e.dataTransfer.items)
            .filter((i) => i.kind === `file`)
            .map((f) => f.getAsFile())
        : Array.from(e.dataTransfer.files)
    } else {
      files = Array.from(e.target.files)
      this.fileinput.current.value = null
    }

    const { skipDuplicate = false, existedFiles = [] } = this.props

    const newFiles = []
    const unsupportedFiles = []

    //console.log('Files dropped:', files)

    for (const file of files) {
      // 1. Check MIME type
      const isSupported = allowedMimeTypes.includes(file.type)
      if (!isSupported) {
        unsupportedFiles.push(file.name)
        continue
      }

      // 2. Check for duplication in current state
      const isDuplicate = skipDuplicate && this.state.names.includes(file.name)

      // 3. Check for existence in passed-in existedFiles
      const isAlreadyExisted = existedFiles && existedFiles.includes(file.name)

      // 4. Skip if either duplicate or existed
      if (isDuplicate || isAlreadyExisted) { 
        console.log(`Skipping duplicate or already existed file: ${file.name}`)
        const logger = new SystemLogger({ })
        logger.log(
        `Skipping duplicate or already existed file: ${file.name}`
        )
        continue
      }

      newFiles.push(file)
    }

    //console.log('unsupported files', unsupportedFiles)
    //console.log('new files to upload', newFiles)

    // ✅ Show alert ONLY for unsupported file types
    if (unsupportedFiles.length > 0) {
      const logger = new SystemLogger({ })
      logger.log(
      `Some files were rejected due to unsupported types:\n\n${unsupportedFiles.join('\n')}`
      )
      //alert(`Some files were rejected due to unsupported types:\n\n${unsupportedFiles.join('\n')}`)
    }

    if (newFiles.length > 0) {
      console.log('new files to upload 2')
      this.setState(prevState => ({
        backgroundColor: `lightgrey`,
        color: `black`,
        files: [...prevState.files, ...newFiles],
        names: [...prevState.names, ...newFiles.map(file => file.name)],
      }))
      this.upload()
    }
    else {
      //logger.event(`No new files to upload`)
      console.log('No new files to upload')
      this.setState({backgroundColor: `lightgrey`, color: `black`, files: []})
    }

    if (e.dataTransfer) {
      if (e.dataTransfer.items) e.dataTransfer.items.clear()
      else e.dataTransfer.clearData()
    }
  }

  // changes the backgroundColor of the file drop window when files dragged over it
  dragover = (e) => {
    e.preventDefault()
    this.setState({backgroundColor: `black`, color: `white`, dragOver: true})
  }

  // restores original backgroundColor on drag leave
  dragleave = (e) => {
    e.preventDefault()
    this.setState({backgroundColor: `lightgrey`, color: `black`, dragOver: false})
  }

  getAccept = () => {
    const accepts = {
      photo: 'image/png,image/jpeg',
      thumbnail: 'image/png,image/jpeg',
      avatar: 'image/png,image/jpeg',
      logo: 'image/png,image/jpeg',
      report: 'application/pdf"',
      file: allowedMimeTypes.join(','),
    }
    return accepts[this.props.type] || '*'
  }

  render = () => {
    let {drop, dragover, dragleave} = this //	upload,
    let {files} = this.state
    let {helperMessage: Message} = this.props

    return (
      <div
        className={`flex-column full-height full-width align-center justify-center file-uploader ${
          this.state.dragOver && `dragover`
        }`}
        style={this.props.disabled ? {pointerEvents: 'none', opacity: '0.4'} : {}}
      >
        <div
          className="files full-height full-width align-center justify-center flex-column margin-1"
          style={{
            flex: 1,
            backgroundColor: this.state.backgroundColor,
            color: this.state.color,
            padding: `5rem`,
          }}
          onDrop={drop}
          onDragOver={dragover}
          onDragLeave={dragleave}
        >
          {files.length === 0 && (
            <div style={{fontSize: `150%`, width: `100%`}}>
              <Message />
            </div>
          )}
          {files.length > 0 && (
            <div>
              <h3>Uploaded</h3>
              {files.map((f, i) => (
                <div className="bold" key={i}>
                  {f.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="upload-button">
          <p>Or...</p>
          <input type="file" onChange={(e) => drop(e)} multiple ref={this.fileinput} accept={this.getAccept()} />
        </div>
      </div>
    )
  }
}
